/**
 * Stores > HQ scorecard at /stores/hq.
 *
 * The daily compliance scorecard: a date picker (defaults to today), a grid of
 * every active store showing name, region, pilot chip, Open/Midday/Close
 * submitted checks, photo count, traffic, transactions, and open questions. A
 * totals strip (reported vs missing) sits above it. Columns sort via the
 * shared SortableHeader, default alphabetical by store name; a filter chip row
 * narrows to Missing a report / All reports in. Date and the selected store
 * live in the URL (`?date=`, `?store=`) so a link survives refresh and can be
 * shared. Clicking a row/card opens a read-only day-detail drawer
 * (ScorecardDayDetail) for that store and date; only the open-questions badge
 * still links to the store's HQ thread. Excel export via the backend endpoint,
 * with a client-side CSV fallback if that endpoint is absent.
 *
 * Gated by stores:hq. 404/503 -> not-available placeholder.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, X, Download, Camera, MessageSquare } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { SortableHeader, gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import {
  useScorecard,
  scorecardExportUrl,
  isNotAvailable,
  denverToday,
  type ScorecardRow,
} from '@/lib/useStores';
import {
  StoresHeader,
  AccessDeniedCard,
  NotAvailableCard,
  ErrorCard,
  secondaryBtnCls,
} from './storesShared';
import {
  SCORECARD_REPORT_FILTERS,
  filterScorecardRows,
  parseScorecardSort,
  sortScorecardRows,
  type ScorecardReportFilter,
  type ScorecardSortColumn,
} from './scorecardSort';
import { ScorecardDayDetail } from './ScorecardDayDetail';

function parseReportFilter(raw: string | null): ScorecardReportFilter | null {
  return raw === 'missing' || raw === 'complete' ? raw : null;
}

function parseStoreParam(raw: string | null): number | null {
  return raw && /^\d+$/.test(raw) ? Number(raw) : null;
}

export function HqScorecard() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.scorecard', user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const today = denverToday();
  const date = searchParams.get('date') || today;
  const selectedStoreNo = parseStoreParam(searchParams.get('store'));
  const reportFilter = parseReportFilter(searchParams.get('report'));
  const { column: sortColumn, dir: sortDir } = parseScorecardSort(searchParams.get('sort'));

  const scoreQ = useScorecard(date);

  const update = (mutate: (sp: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };

  // today is the default, so the URL only carries a date when it differs.
  const setDate = (v: string) => update((sp) => (v && v !== today ? sp.set('date', v) : sp.delete('date')));
  const openStore = (storeNo: number) => update((sp) => sp.set('store', String(storeNo)));
  const closeDrawer = () => update((sp) => sp.delete('store'));
  const setReportFilter = (f: ScorecardReportFilter | null) =>
    update((sp) => (f ? sp.set('report', f) : sp.delete('report')));
  const toggleSort = (col: ScorecardSortColumn) =>
    update((sp) => {
      if (sortColumn !== col) sp.set('sort', `${col}:asc`);
      else if (sortDir === 'asc') sp.set('sort', `${col}:desc`);
      else sp.delete('sort');
    });

  const rows = useMemo(() => {
    const filtered = filterScorecardRows(scoreQ.data?.stores ?? [], reportFilter);
    return sortScorecardRows(filtered, sortColumn, sortDir);
  }, [scoreQ.data, reportFilter, sortColumn, sortDir]);

  const selectedStore =
    selectedStoreNo != null
      ? (scoreQ.data?.stores ?? []).find((r) => r.store_no === selectedStoreNo) ?? null
      : null;

  if (user && !canView) return <AccessDeniedCard what="The compliance scorecard" />;
  if (isNotAvailable(scoreQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <StoresHeader title="Daily scorecard" actions={<ToolPinToggle toolSlug="stores-hq" toolName="Daily scorecard" />} />
        <NotAvailableCard what="The compliance scorecard" />
      </div>
    );
  }

  const totals = scoreQ.data?.totals;

  const exportCsv = () => {
    const header = [
      'Store',
      'Store No',
      'Region',
      'Pilot 16',
      'Opening',
      'Midday',
      'Closing',
      'Photos',
      'Traffic',
      'Transactions',
      'Open questions',
    ];
    const lines = [header.join(',')];
    for (const r of rows) {
      const cells = [
        r.store_name,
        String(r.store_no),
        r.region ?? '',
        r.pilot16 ? 'Yes' : 'No',
        r.open_submitted ? 'Yes' : 'No',
        r.midday_submitted ? 'Yes' : 'No',
        r.close_submitted ? 'Yes' : 'No',
        String(r.photo_count),
        r.traffic != null ? String(r.traffic) : '',
        r.transactions != null ? String(r.transactions) : '',
        String(r.open_questions),
      ].map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c));
      lines.push(cells.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `store-scorecard-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filterBar = (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-end gap-2 sm:gap-3">
        <div className="min-w-0 flex-1 sm:flex-initial">
          <label htmlFor="date" className="mb-1 block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Date
          </label>
          <input
            id="date"
            type="date"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value || today)}
            className="w-full rounded border border-amp-line bg-amp-white px-3 py-2 text-sm text-amp-ink sm:w-auto sm:py-1.5 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          />
        </div>
        <a
          href={scorecardExportUrl(date)}
          className={`${secondaryBtnCls} min-h-[38px] py-2 sm:min-h-0 sm:py-1.5`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Download className="h-3 w-3" /> Export Excel
        </a>
        <button type="button" onClick={exportCsv} className={`${secondaryBtnCls} min-h-[38px] py-2 sm:min-h-0 sm:py-1.5`}>
          <Download className="h-3 w-3" /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {SCORECARD_REPORT_FILTERS.map((f) => (
          <Pill
            key={f.label}
            variant={reportFilter === f.value ? 'selected' : 'outline'}
            aria-pressed={reportFilter === f.value}
            onClick={() => setReportFilter(f.value)}
          >
            {f.label}
          </Pill>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <StoresHeader
        title="Daily scorecard"
        subtitle="Which stores reported opening, midday, and closing today."
        actions={<ToolPinToggle toolSlug="stores-hq" toolName="Daily scorecard" />}
      />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <KpiBox label="Reported" value={totals?.reported} tone="text-green-700 dark:text-green-300" />
        <KpiBox label="Missing" value={totals?.missing} tone="text-red-700 dark:text-red-300" />
        <KpiBox label="Active stores" value={scoreQ.data ? scoreQ.data.stores.length : undefined} />
      </div>

      <DataGridShell
        total={rows.length}
        loaded={rows.length}
        countNoun="stores"
        toolbar={filterBar}
      >
        {/* Desktop / tablet-wide (>=768px): the full scorecard table. */}
        <table className="hidden min-w-full text-sm md:table">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader
                label="Store"
                sorted={sortColumn === 'store' ? sortDir : null}
                onToggle={() => toggleSort('store')}
                thClassName="px-3 py-2"
              />
              <th className="px-3 py-2 text-left font-medium w-[120px]">Region</th>
              <SortableHeader
                label="Open"
                align="right"
                sorted={sortColumn === 'open' ? sortDir : null}
                onToggle={() => toggleSort('open')}
                thClassName="px-3 py-2 w-[70px]"
              />
              <SortableHeader
                label="Midday"
                align="right"
                sorted={sortColumn === 'midday' ? sortDir : null}
                onToggle={() => toggleSort('midday')}
                thClassName="px-3 py-2 w-[70px]"
              />
              <SortableHeader
                label="Close"
                align="right"
                sorted={sortColumn === 'close' ? sortDir : null}
                onToggle={() => toggleSort('close')}
                thClassName="px-3 py-2 w-[70px]"
              />
              <SortableHeader
                label="Photos"
                align="right"
                sorted={sortColumn === 'photos' ? sortDir : null}
                onToggle={() => toggleSort('photos')}
                thClassName="px-3 py-2 w-[80px]"
              />
              <SortableHeader
                label="Traffic"
                align="right"
                sorted={sortColumn === 'traffic' ? sortDir : null}
                onToggle={() => toggleSort('traffic')}
                thClassName="px-3 py-2 w-[90px]"
              />
              <SortableHeader
                label="Trans."
                align="right"
                sorted={sortColumn === 'transactions' ? sortDir : null}
                onToggle={() => toggleSort('transactions')}
                thClassName="px-3 py-2 w-[100px]"
              />
              <SortableHeader
                label="Questions"
                align="right"
                sorted={sortColumn === 'questions' ? sortDir : null}
                onToggle={() => toggleSort('questions')}
                thClassName="px-3 py-2 w-[90px]"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {scoreQ.isLoading ? (
              <tr><td colSpan={9} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading scorecard...</td></tr>
            ) : scoreQ.error ? (
              <tr><td colSpan={9} className="px-4 py-6"><ErrorCard message={scoreQ.error.message} /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No active stores.</td></tr>
            ) : (
              rows.map((r) => <ScoreRow key={r.store_no} r={r} onOpen={() => openStore(r.store_no)} />)
            )}
          </tbody>
        </table>

        {/* Phone / narrow (<768px): one compact card per store. */}
        <div className="md:hidden">
          {scoreQ.isLoading ? (
            <div className="space-y-2 p-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-28 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
              ))}
            </div>
          ) : scoreQ.error ? (
            <div className="p-3"><ErrorCard message={scoreQ.error.message} /></div>
          ) : rows.length === 0 ? (
            <p className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No active stores.</p>
          ) : (
            <div className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {rows.map((r) => <ScoreCard key={r.store_no} r={r} onOpen={() => openStore(r.store_no)} />)}
            </div>
          )}
        </div>
      </DataGridShell>

      {selectedStore && (
        <ScorecardDayDetail
          storeNo={selectedStore.store_no}
          storeName={selectedStore.store_name}
          date={date}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}

function ScoreRow({ r, onOpen }: { r: ScorecardRow; onOpen: () => void }) {
  return (
    <tr
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`View ${r.store_name}'s checklist for this date`}
      className="cursor-pointer hover:bg-amp-cream-warm focus:outline-none focus:ring-2 focus:ring-amp-olive/40 dark:hover:bg-amp-dark-surface-warm"
    >
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        <span className="font-medium">{r.store_name}</span>
        {r.pilot16 && (
          <span className="ml-2 align-middle">
            <Pill tone="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Pilot 16</Pill>
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{r.region ?? '--'}</td>
      <td className="px-3 py-2 text-right"><PhaseCheck ok={r.open_submitted} /></td>
      <td className="px-3 py-2 text-right"><PhaseCheck ok={r.midday_submitted} /></td>
      <td className="px-3 py-2 text-right"><PhaseCheck ok={r.close_submitted} /></td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
        {r.photo_count > 0 ? (
          <span className="inline-flex items-center gap-1 justify-end">
            <Camera className="h-3 w-3" aria-hidden /> {r.photo_count}
          </span>
        ) : (
          <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
        {r.traffic != null ? r.traffic.toLocaleString() : '--'}
      </td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
        {r.transactions != null ? r.transactions.toLocaleString() : '--'}
      </td>
      <td className="px-3 py-2 text-right">
        {r.open_questions > 0 ? (
          <Link
            to={`/stores/hq/threads/${r.store_no}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-block"
          >
            <Pill tone="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{r.open_questions}</Pill>
          </Link>
        ) : (
          <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">0</span>
        )}
      </td>
    </tr>
  );
}

// Compact per-store card for phones (<768px). Store name + region on top, the
// three phase pills (filled when submitted), photos/traffic/transactions inline,
// and an open-questions badge (links to the thread; everything else opens the
// day-detail drawer).
function ScoreCard({ r, onOpen }: { r: ScorecardRow; onOpen: () => void }) {
  return (
    <div
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`View ${r.store_name}'s checklist for this date`}
      className="cursor-pointer px-4 py-3 hover:bg-amp-cream-warm focus:outline-none focus:ring-2 focus:ring-amp-olive/40 dark:hover:bg-amp-dark-surface-warm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold text-amp-ink dark:text-amp-dark-ink">
            {r.store_name}
          </p>
          <p className="text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {r.region ?? '--'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {r.pilot16 && (
            <Pill tone="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Pilot 16</Pill>
          )}
          {r.open_questions > 0 && (
            <Link
              to={`/stores/hq/threads/${r.store_no}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex min-h-[24px] items-center gap-1 rounded-pill bg-amber-100 px-2 py-1 text-[12px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
            >
              <MessageSquare className="h-3 w-3" aria-hidden /> {r.open_questions}
            </Link>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <PhasePill label="Open" ok={r.open_submitted} />
        <PhasePill label="Mid" ok={r.midday_submitted} />
        <PhasePill label="Close" ok={r.close_submitted} />
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <span className="inline-flex items-center gap-1">
          <Camera className="h-3.5 w-3.5" aria-hidden /> {r.photo_count} {r.photo_count === 1 ? 'photo' : 'photos'}
        </span>
        <span>
          Traffic <span className="font-medium text-amp-ink dark:text-amp-dark-ink">{r.traffic != null ? r.traffic.toLocaleString() : '--'}</span>
        </span>
        <span>
          Trans. <span className="font-medium text-amp-ink dark:text-amp-dark-ink">{r.transactions != null ? r.transactions.toLocaleString() : '--'}</span>
        </span>
      </div>
    </div>
  );
}

// A filled pill when the phase was submitted, an outline pill when it was not.
function PhasePill({ label, ok }: { label: string; ok: boolean }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-pill bg-green-100 px-2.5 py-1 text-[12px] font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300">
      <Check className="h-3 w-3" aria-hidden /> {label}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-pill border border-amp-line px-2.5 py-1 text-[12px] font-semibold text-amp-ink-muted dark:border-amp-dark-line dark:text-amp-dark-ink-muted">
      <X className="h-3 w-3" aria-hidden /> {label}
    </span>
  );
}

function PhaseCheck({ ok }: { ok: boolean }) {
  return ok ? (
    <Check className="ml-auto h-4 w-4 text-green-600 dark:text-green-400" aria-label="Submitted" />
  ) : (
    <X className="ml-auto h-4 w-4 text-amp-ink-muted/50 dark:text-amp-dark-ink-muted/50" aria-label="Not submitted" />
  );
}

function KpiBox({ label, value, tone }: { label: string; value?: number; tone?: string }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="mb-1 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">{label}</p>
      <p className={`text-2xl font-semibold ${tone ?? 'text-amp-ink dark:text-amp-dark-ink'}`}>
        {value != null ? value.toLocaleString() : '--'}
      </p>
    </div>
  );
}
